/* Product bar — the buying bar stuck to the bottom of the window in mobile,
 * and the sheet of sizes it opens.
 *
 * Why a file of its own rather than a {% javascript %} block in the snippet:
 * `shopify theme dev` serves the compiled bundles truncated past a threshold
 * that moves from one run to the next, and a bundle that does not parse
 * registers nothing at all. Files in `assets/` are served whole. Same rule as
 * the other components of this page.
 *
 * Why not in `cart.js`, next to `<product-form>`: opening a sheet is not cart
 * mechanics, and `cart.js` is written to be copied from one project to the
 * next with its own markup contract. The two components sit on the same
 * markup and ignore each other — `<product-form>` owns the form and the Ajax
 * API, this owns where the form is shown.
 *
 * What moves, and what does not. Nothing is rendered twice: the title, the
 * price and the form are the same nodes in both layouts. The bar itself is
 * `display: contents` in desktop, so its children lay out as if it were not
 * there; below the breakpoint it becomes a real box at the bottom of the
 * window. This file opens and closes the sheet, keeps the labels of the opener
 * and of the sheet's button in step with the size chosen, places the bar, and
 * says when it is ready — the stylesheet leaves the sizes in the flow until
 * then, so a page without JavaScript keeps them.
 *
 * Where the bar sits. On marineserre.com it is a sticky element of the page
 * wrapper, placed in the source between the materials and the related
 * products: it rides the bottom of the window from the top of the page, then
 * comes to rest in that slot and scrolls away with it. Here the materials are
 * another section, which a sticky element of the product section cannot
 * outlive — it would leave the window as soon as the product card does. So the
 * script does by hand what `sticky` does there:
 *
 *   - it lays an empty slot of the bar's height right after the materials
 *     section (after the product section when there is none), the room the bar
 *     takes when it rests;
 *   - the bar is `position: fixed` at the bottom of the window (`data-bar-docked`)
 *     as long as that slot has not come up past the bottom edge;
 *   - once it has, the bar turns `absolute`, laid exactly over the slot
 *     (`is-resting`), and scrolls with the page.
 *
 * The switch is driven by an IntersectionObserver on the slot, not by a scroll
 * listener: the bar moves with the compositor in both states, never one frame
 * behind the finger. A ResizeObserver keeps the slot as tall as the bar (the
 * stock mention can grow it) and the resting position right when the content
 * above it changes height (materials products arriving, images loading).
 *
 * Markup contract (what the script looks for in the DOM):
 *
 *   <product-bar> inside a `.shopify-section`, containing the heading and the
 *   form, and within them:
 *     [data-sizes-sheet]        the <dialog> holding the sizes
 *     [data-sizes-open]         the opener, shipped with `hidden`
 *     [data-sizes-close]        closes the sheet
 *     [data-sizes-value]        the element carrying the size being shown
 *     [data-form-size]          the size buttons, read for their label, their
 *                               `data-available` and their `data-add-label`
 *     [data-sizes-submit-label] the label of the sheet's own green button
 *   and, in the next section when it is the materials, [data-materials].
 *
 * Class names are free: nothing here reads one.
 */

class ProductBar extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('[data-sizes-sheet]');
    this.opener = this.querySelector('[data-sizes-open]');
    this.value = this.querySelector('[data-sizes-value]');
    this.sheetLabel = this.querySelector('[data-sizes-submit-label]');
    this.form = this.querySelector('form');
    this.productForm = this.querySelector('product-form');

    this.narrow = window.matchMedia('(max-width: 900px)');
    this.narrow.addEventListener('change', this.onBreakpoint);
    this.place();

    if (!this.sheet || !this.opener) return;

    /* The size being worn is where the keyboard lands: `showModal` would
       otherwise put the focus on the size guide, first in the sheet. */
    this.opener.addEventListener('click', () => {
      this.sheet.showModal();
      this.sheet.querySelector('[data-form-size][aria-pressed="true"]')?.focus();
    });
    this.querySelector('[data-sizes-close]')?.addEventListener('click', () => this.sheet.close());

    /* A click landing on the dialog itself is a click on the veil: the
       content fills the box. */
    this.sheet.addEventListener('click', (event) => {
      if (event.target === this.sheet) this.sheet.close();
    });

    /* Listening on the sheet rather than on each button: `<product-form>`
       owns those buttons, and one listener here cannot fall out of step with
       them. The labels are read from the button rather than from the form, so
       the two components share nothing but the DOM.

       Choosing a size no longer closes the sheet, as on the original: the
       sheet carries its own green button, which says what it will add. */
    this.sheet.addEventListener('click', (event) => {
      const size = event.target.closest('[data-form-size]');
      if (!size) return;

      if (this.value) this.value.textContent = size.textContent.trim();
      if (this.sheetLabel) {
        this.sheetLabel.textContent = size.dataset.available === 'true'
          ? size.dataset.addLabel
          : this.productForm?.dataset.labelBackInStock || '';
      }
    });

    /* The sheet's button submits the form it sits in; `<product-form>` takes
       it from there. The sheet steps aside so that what comes next — the
       "added to bag" panel, an error under the bar's button — is in view. */
    this.form?.addEventListener('submit', () => {
      if (this.sheet.open) this.sheet.close();
    });

    /* Last: the opener does nothing until everything above is wired, and the
       stylesheet keeps the sizes in the flow until this attribute appears. */
    this.opener.hidden = false;
    this.dataset.barReady = '';
  }

  disconnectedCallback() {
    this.narrow?.removeEventListener('change', this.onBreakpoint);
    this.undock();
  }

  onBreakpoint = () => this.place();

  place() {
    this.setSheetRole();
    if (this.narrow.matches) this.dock();
    else this.undock();
  }

  /* The sheet is only a dialog below the breakpoint. Above it the stylesheet
     lays the very same `<dialog>` flat in the column, where it is never
     opened — and an element that renders as an ordinary block still carries
     the implicit `role="dialog"` of its tag, so a screen reader announced a
     dialog around the sizes on every desktop product sheet. The role is
     therefore carried by the viewport, like the layout it describes: `group`
     while the sizes are part of the page, the tag's own `dialog` once they
     are a sheet one opens. */
  setSheetRole() {
    if (!this.sheet) return;
    /* The name goes with the role: the sheet is "Size" when it opens as a
       dialog, and nameless as a group in the column — the group of the sizes
       it holds is already named "Size", and a screen reader would hear it
       twice. The label is kept to be put back below the breakpoint. */
    if (this.sheetName === undefined) this.sheetName = this.sheet.getAttribute('aria-label');
    if (this.narrow.matches) {
      this.sheet.removeAttribute('role');
      if (this.sheetName) this.sheet.setAttribute('aria-label', this.sheetName);
    } else {
      this.sheet.setAttribute('role', 'group');
      this.sheet.removeAttribute('aria-label');
    }
  }

  dock() {
    if (this.placeholder) return;

    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    /* The slot follows the materials when they come next, whether or not they
       show anything: a hidden card leaves an empty section, and the slot then
       sits right under the product card, as the original's does. */
    const next = this.section.nextElementSibling;
    this.anchor = next && next.querySelector('[data-materials]') ? next : this.section;

    /* `placeholder`, not `slot`: an element already has a `slot` property,
       the reflection of the attribute, which would turn the box into a
       string. */
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'product__bar-slot';
    this.anchor.after(this.placeholder);

    this.dataset.barDocked = '';
    this.measure();

    this.resizer = new ResizeObserver(() => this.measure());
    [this, this.section, this.anchor].forEach((element) => this.resizer.observe(element));

    /* Two thresholds, so that the callback runs both when the slot starts to
       show and when it is entirely in view — the moment its bottom edge
       crosses the bottom of the window. Which side it is on is then read from
       the rectangles, not from `isIntersecting`: a slot scrolled away above
       the window does not intersect, and the bar must stay at rest there.
       `rootBounds` is null in a cross-origin frame (the theme editor), where
       the height of the window answers the same question. */
    this.watcher = new IntersectionObserver(([entry]) => {
      const bottom = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
      this.setResting(entry.boundingClientRect.bottom <= bottom + 1);
    }, { threshold: [0, 1] });
    this.watcher.observe(this.placeholder);
  }

  undock() {
    this.watcher?.disconnect();
    this.resizer?.disconnect();
    this.placeholder?.remove();
    this.watcher = this.resizer = this.placeholder = null;
    this.resting = false;
    this.classList.remove('is-resting');
    this.style.removeProperty('top');
    delete this.dataset.barDocked;
  }

  setResting(resting) {
    this.resting = resting;
    this.classList.toggle('is-resting', resting);
    this.measure();
  }

  /* The slot takes the height of the bar; at rest the bar takes the place of
     the slot, measured from the top of its own section, which is the box its
     `absolute` position is resolved against. Out of rest the `top` is
     removed: on a fixed box it would fight with `bottom` and stretch it. */
  measure() {
    if (!this.placeholder) return;

    this.placeholder.style.height = `${this.offsetHeight}px`;

    if (this.resting) {
      const top = this.placeholder.getBoundingClientRect().top - this.section.getBoundingClientRect().top;
      this.style.top = `${top}px`;
    } else {
      this.style.removeProperty('top');
    }
  }
}

if (!customElements.get('product-bar')) {
  customElements.define('product-bar', ProductBar);
}
